import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/auth';

/**
 * pre236 — Siegel-Zertifikat-Verwaltung in den Einstellungen (Ablage & Ordner).
 * Der Call zeigt nur noch den Status (Pille); Aktivierung/Upload/Entfernen passiert hier.
 * Drei Wege:
 *  - Zentrales Mandanten-Zertifikat aktivieren (Chiffrat vom Server, Passphrase → lokal,
 *    nicht-exportierbar in IndexedDB)
 *  - P12/PFX-Datei direkt laden (Fallback ohne zentrale Hinterlegung)
 *  - Lokalen Schlüssel entfernen
 * Änderungen werden per 'bamoa:seal-changed' an offene Views (InCall-Pille) gemeldet.
 */
export function SealCertSettings() {
  const [localLabel, setLocalLabel] = useState<string | null>(null);
  const [tenantLabel, setTenantLabel] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try { const m = await import('@/lib/sealCryptoKey'); setLocalLabel(await m.getStoredSealLabel()); } catch { /* */ }
    try {
      const r = await apiFetch('/api/seal-cert/status');
      if (r.ok) { const j = await r.json(); setTenantLabel(j?.label ?? 'Siegel-Zertifikat'); } else setTenantLabel(null);
    } catch { setTenantLabel(null); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const announce = () => window.dispatchEvent(new CustomEvent('bamoa:seal-changed'));

  const activateTenant = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiFetch('/api/seal-cert');
      if (!r.ok) { setMsg('Kein zentrales Zertifikat hinterlegt.'); return; }
      const enc = await r.json();
      const passphrase = window.prompt(`Mandanten-Passphrase für „${enc.label ?? 'Siegel-Zertifikat'}“:`);
      if (passphrase == null) return;
      const { decryptP12 } = await import('@/lib/sealCertCrypto');
      const p12 = await decryptP12(enc, passphrase);
      const { importP12ToStore } = await import('@/lib/sealCryptoKey');
      let label: string;
      try { label = await importP12ToStore(p12, passphrase, enc.uploadedAt); }
      catch { label = await importP12ToStore(p12, '', enc.uploadedAt); }
      setLocalLabel(label); announce();
      setMsg('Aktiviert — Schlüssel liegt jetzt nicht-exportierbar in diesem Browser.');
    } catch (e) {
      console.error('[seal-settings] Aktivierung fehlgeschlagen', e);
      setMsg('Entschlüsselung fehlgeschlagen — Passphrase prüfen.');
    } finally { setBusy(false); setTimeout(() => setMsg(''), 6000); }
  };

  const loadFile = async (file: File) => {
    setBusy(true); setMsg('');
    try {
      const passphrase = window.prompt('Passwort der P12/PFX-Datei:') ?? '';
      const { importP12ToStore } = await import('@/lib/sealCryptoKey');
      const label = await importP12ToStore(await file.arrayBuffer(), passphrase);
      setLocalLabel(label); announce();
      setMsg('Geladen — Schlüssel liegt nicht-exportierbar in diesem Browser.');
    } catch (e) {
      console.error('[seal-settings] Datei-Import fehlgeschlagen', e);
      setMsg('Zertifikat konnte nicht geladen werden — Passwort/Format prüfen.');
    } finally { setBusy(false); setTimeout(() => setMsg(''), 6000); }
  };

  const remove = async () => {
    if (!window.confirm('Lokalen Siegel-Schlüssel aus diesem Browser entfernen?')) return;
    const { clearStoredSealKey } = await import('@/lib/sealCryptoKey');
    await clearStoredSealKey();
    setLocalLabel(null); announce();
  };

  return (
    <div className="settings-section">
      <div className="settings-section-title">Siegel-Zertifikat (PAdES)</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted, #8aa)', lineHeight: 1.5, marginBottom: 10 }}>
        Versiegelt signierte Dokumente automatisch mit einer digitalen Signatur der Bank.
        Der Schlüssel wird nicht-exportierbar in diesem Browser gespeichert; die Aktivierung
        ist einmal pro Arbeitsplatz nötig.
      </div>

      {localLabel ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--status-available, #7ee787)' }}>{'🔒'} Aktiv: <b>{localLabel}</b></span>
          <button onClick={() => void remove()} disabled={busy} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #a33', background: 'transparent', color: '#e88', cursor: 'pointer', fontSize: 12.5 }}>Entfernen</button>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--muted,#8aa)', marginBottom: 8 }}>Kein Schlüssel in diesem Browser aktiv.</div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {tenantLabel && (
          <button onClick={() => void activateTenant()} disabled={busy} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'var(--accent, #e2001a)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {localLabel ? 'Zentrales Siegel neu aktivieren' : 'Zentrales Siegel aktivieren'} ({tenantLabel})
          </button>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={busy} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hairline,#333)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>
          P12/PFX-Datei laden …
        </button>
      </div>
      <input ref={fileRef} type="file" accept=".p12,.pfx,application/x-pkcs12" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void loadFile(f); }} />
      {msg && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted,#8aa)' }}>{msg}</div>}
    </div>
  );
}
